import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Upload, Building, Phone, Mail, Globe, MapPin, Palette, Image, FileText, RefreshCw, User, Smartphone, Facebook, ExternalLink } from 'lucide-react';
import CompanyLogo from '../components/CompanyLogo';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../i18n/LanguageContext';
import { COMPANY, DEFAULT_TERMS } from '../config/company';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Settings = () => {
  const { language } = useLanguage();
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logoPreview, setLogoPreview] = useState(null);
  
  const [settings, setSettings] = useState({
    // Contractor Info
    contractorName: '',
    // Company Info
    name: COMPANY.name,
    license: COMPANY.license,
    license2: '',
    license3: '',
    license4: '',
    license5: '',
    tagline: COMPANY.tagline,
    // Address
    address: COMPANY.address,
    city: COMPANY.city,
    state: COMPANY.state,
    zip: COMPANY.zip,
    // Contact
    phone: COMPANY.phone,
    mobile: '',
    email: COMPANY.email,
    website: COMPANY.website,
    // Social Links
    facebookUrl: '',
    googleBusinessUrl: '',
    // Branding
    defaultTaxRate: COMPANY.defaultTaxRate,
    primaryColor: COMPANY.primaryColor,
    secondaryColor: COMPANY.secondaryColor,
    // Terms
    termsEn: DEFAULT_TERMS.en,
    termsEs: DEFAULT_TERMS.es,
    // Introduction & Reviews
    introductionLetter: '',
    reviews: ''
  });

  // Load settings from database on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/settings`);
        if (response.ok) {
          const data = await response.json();
          setSettings(prev => ({ ...prev, ...data }));
          if (data.logo) {
            setLogoPreview(data.logo);
          }
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        const savedSettings = localStorage.getItem('proestimate_settings');
        if (savedSettings) {
          setSettings(JSON.parse(savedSettings));
        }
        const savedLogo = localStorage.getItem('proestimate_logo');
        if (savedLogo) {
          setLogoPreview(savedLogo);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(language === 'es' ? 'El archivo es muy grande. Máximo 2MB.' : 'File is too large. Maximum 2MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const settingsToSave = { ...settings };
      if (logoPreview) {
        settingsToSave.logo = logoPreview;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settingsToSave)
      });
      
      if (response.ok) {
        localStorage.setItem('proestimate_settings', JSON.stringify(settingsToSave));
        if (logoPreview) {
          localStorage.setItem('proestimate_logo', logoPreview);
        }
        toast.success(language === 'es' ? '¡Configuración guardada!' : 'Settings saved!');
        setTimeout(() => window.location.reload(), 500);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(language === 'es' ? 'Error al guardar' : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 shadow-lg sticky top-0 z-50">
        <nav className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/" className="hover:opacity-80 transition-opacity">
              <CompanyLogo className="h-10 w-auto" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-white">
                {language === 'es' ? 'Configuración' : 'Settings'}
              </h1>
              <p className="text-gray-400 text-sm">
                {language === 'es' ? 'Personaliza tu información' : 'Customize your information'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <LanguageToggle />
            <Link to="/admin">
              <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {language === 'es' ? 'Volver' : 'Back'}
              </Button>
            </Link>
            <Button onClick={handleSave} style={{ backgroundColor: COMPANY.primaryColor }} className="text-white" disabled={saving}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? (language === 'es' ? 'Guardando...' : 'Saving...') : (language === 'es' ? 'Guardar' : 'Save')}
            </Button>
          </div>
        </nav>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Contractor Information */}
          <Card>
            <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
              <CardTitle className="flex items-center">
                <User className="mr-2 h-5 w-5" />
                {language === 'es' ? 'Información del Contratista' : 'Contractor Information'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label>{language === 'es' ? 'Nombre del Contratista' : 'Contractor Name'}</Label>
                <Input 
                  value={settings.contractorName} 
                  onChange={(e) => handleChange('contractorName', e.target.value)} 
                  placeholder="John Smith"
                  data-testid="contractor-name"
                />
              </div>
              <div>
                <Label>{language === 'es' ? 'Nombre de la Empresa' : 'Company Name'}</Label>
                <Input 
                  value={settings.name} 
                  onChange={(e) => handleChange('name', e.target.value)} 
                  placeholder="ABC Construction LLC"
                  data-testid="company-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Licencia Principal' : 'Primary License'}</Label>
                  <Input 
                    value={settings.license} 
                    onChange={(e) => handleChange('license', e.target.value)} 
                    placeholder="CGC1234567"
                  />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Licencia 2' : 'License 2'}</Label>
                  <Input 
                    value={settings.license2} 
                    onChange={(e) => handleChange('license2', e.target.value)} 
                    placeholder="CCC1234567"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Licencia 3' : 'License 3'}</Label>
                  <Input 
                    value={settings.license3} 
                    onChange={(e) => handleChange('license3', e.target.value)} 
                    placeholder="CAC1234567"
                  />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Licencia 4' : 'License 4'}</Label>
                  <Input 
                    value={settings.license4} 
                    onChange={(e) => handleChange('license4', e.target.value)} 
                    placeholder="EC1234567"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Licencia 5' : 'License 5'}</Label>
                  <Input 
                    value={settings.license5} 
                    onChange={(e) => handleChange('license5', e.target.value)} 
                    placeholder="Other License"
                  />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Eslogan' : 'Tagline'}</Label>
                  <Input 
                    value={settings.tagline} 
                    onChange={(e) => handleChange('tagline', e.target.value)} 
                    placeholder="Licensed General Contractor"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logo Upload */}
          <Card>
            <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
              <CardTitle className="flex items-center">
                <Image className="mr-2 h-5 w-5" />
                {language === 'es' ? 'Logo de la Empresa' : 'Company Logo'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-48 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo Preview" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <div className="text-center text-gray-400">
                      <Image className="h-10 w-10 mx-auto mb-2" />
                      <p className="text-sm">{language === 'es' ? 'Sin logo' : 'No logo'}</p>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Subir Logo' : 'Upload Logo'}
                </Button>
                <p className="text-xs text-gray-500">{language === 'es' ? 'PNG, JPG hasta 2MB' : 'PNG, JPG up to 2MB'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
              <CardTitle className="flex items-center">
                <MapPin className="mr-2 h-5 w-5" />
                {language === 'es' ? 'Dirección' : 'Address'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label>{language === 'es' ? 'Dirección' : 'Street Address'}</Label>
                <Input 
                  value={settings.address} 
                  onChange={(e) => handleChange('address', e.target.value)} 
                  placeholder="123 Main Street"
                  data-testid="address"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Ciudad' : 'City'}</Label>
                  <Input value={settings.city} onChange={(e) => handleChange('city', e.target.value)} placeholder="Naples" />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Estado' : 'State'}</Label>
                  <Input value={settings.state} onChange={(e) => handleChange('state', e.target.value)} placeholder="FL" />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Código Postal' : 'ZIP'}</Label>
                  <Input value={settings.zip} onChange={(e) => handleChange('zip', e.target.value)} placeholder="34120" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
              <CardTitle className="flex items-center">
                <Phone className="mr-2 h-5 w-5" />
                {language === 'es' ? 'Información de Contacto' : 'Contact Information'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center"><Phone className="mr-2 h-4 w-4" />{language === 'es' ? 'Teléfono' : 'Phone'}</Label>
                  <Input 
                    value={settings.phone} 
                    onChange={(e) => handleChange('phone', e.target.value)} 
                    placeholder="555-123-4567"
                    data-testid="phone"
                  />
                </div>
                <div>
                  <Label className="flex items-center"><Smartphone className="mr-2 h-4 w-4" />{language === 'es' ? 'Móvil' : 'Mobile'}</Label>
                  <Input 
                    value={settings.mobile} 
                    onChange={(e) => handleChange('mobile', e.target.value)} 
                    placeholder="555-987-6543"
                    data-testid="mobile"
                  />
                </div>
              </div>
              <div>
                <Label className="flex items-center"><Mail className="mr-2 h-4 w-4" />{language === 'es' ? 'Correo Electrónico' : 'Email Address'}</Label>
                <Input 
                  value={settings.email} 
                  onChange={(e) => handleChange('email', e.target.value)} 
                  placeholder="info@yourcompany.com" 
                  type="email"
                  data-testid="email"
                />
              </div>
              <div>
                <Label className="flex items-center"><Globe className="mr-2 h-4 w-4" />{language === 'es' ? 'Sitio Web' : 'Website'}</Label>
                <Input 
                  value={settings.website} 
                  onChange={(e) => handleChange('website', e.target.value)} 
                  placeholder="www.yourcompany.com"
                  data-testid="website"
                />
              </div>
            </CardContent>
          </Card>

          {/* Social Links */}
          <Card>
            <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
              <CardTitle className="flex items-center">
                <ExternalLink className="mr-2 h-5 w-5" />
                {language === 'es' ? 'Enlaces Sociales' : 'Social Links'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label className="flex items-center"><Facebook className="mr-2 h-4 w-4" />{language === 'es' ? 'Enlace de Facebook' : 'Facebook Link'}</Label>
                <Input 
                  value={settings.facebookUrl} 
                  onChange={(e) => handleChange('facebookUrl', e.target.value)} 
                  placeholder="https://facebook.com/yourpage"
                  data-testid="facebook-url"
                />
              </div>
              <div>
                <Label className="flex items-center">
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                  </svg>
                  {language === 'es' ? 'Perfil de Google Business' : 'Google Business Profile'}
                </Label>
                <Input 
                  value={settings.googleBusinessUrl} 
                  onChange={(e) => handleChange('googleBusinessUrl', e.target.value)} 
                  placeholder="https://g.page/yourbusiness"
                  data-testid="google-business-url"
                />
              </div>
            </CardContent>
          </Card>

          {/* Branding */}
          <Card>
            <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
              <CardTitle className="flex items-center">
                <Palette className="mr-2 h-5 w-5" />
                {language === 'es' ? 'Marca y Colores' : 'Branding & Colors'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Color Primario' : 'Primary Color'}</Label>
                  <div className="flex items-center space-x-2">
                    <input type="color" value={settings.primaryColor} onChange={(e) => handleChange('primaryColor', e.target.value)} className="w-12 h-10 rounded cursor-pointer" />
                    <Input value={settings.primaryColor} onChange={(e) => handleChange('primaryColor', e.target.value)} className="flex-1" />
                  </div>
                </div>
                <div>
                  <Label>{language === 'es' ? 'Color Secundario' : 'Secondary Color'}</Label>
                  <div className="flex items-center space-x-2">
                    <input type="color" value={settings.secondaryColor} onChange={(e) => handleChange('secondaryColor', e.target.value)} className="w-12 h-10 rounded cursor-pointer" />
                    <Input value={settings.secondaryColor} onChange={(e) => handleChange('secondaryColor', e.target.value)} className="flex-1" />
                  </div>
                </div>
              </div>
              <div>
                <Label>{language === 'es' ? 'Tasa de Impuesto Predeterminada (%)' : 'Default Tax Rate (%)'}</Label>
                <Input type="number" value={settings.defaultTaxRate} onChange={(e) => handleChange('defaultTaxRate', parseFloat(e.target.value) || 0)} step="0.5" />
              </div>
            </CardContent>
          </Card>

          {/* Terms and Conditions */}
          <Card className="lg:col-span-2">
            <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
              <CardTitle className="flex items-center">
                <FileText className="mr-2 h-5 w-5" />
                {language === 'es' ? 'Términos y Condiciones Predeterminados' : 'Default Terms and Conditions'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <Label className="mb-2 block">English Terms</Label>
                  <Textarea value={settings.termsEn} onChange={(e) => handleChange('termsEn', e.target.value)} rows={12} className="font-mono text-sm" />
                </div>
                <div>
                  <Label className="mb-2 block">Términos en Español</Label>
                  <Textarea value={settings.termsEs} onChange={(e) => handleChange('termsEs', e.target.value)} rows={12} className="font-mono text-sm" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Introduction Letter */}
          <Card className="lg:col-span-2">
            <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
              <CardTitle className="flex items-center">
                <FileText className="mr-2 h-5 w-5" />
                {language === 'es' ? 'Carta de Introducción' : 'Introduction Letter'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500 mb-3">
                {language === 'es' 
                  ? 'Esta carta aparecerá al inicio de sus propuestas. Déjela en blanco si no desea incluirla.'
                  : 'This letter will appear at the beginning of your proposals. Leave blank if you don\'t want to include it.'}
              </p>
              <Textarea 
                value={settings.introductionLetter} 
                onChange={(e) => handleChange('introductionLetter', e.target.value)} 
                rows={10} 
                className="font-mono text-sm"
                placeholder={language === 'es' 
                  ? 'Escriba su carta de introducción aquí...'
                  : 'Write your introduction letter here...'}
              />
            </CardContent>
          </Card>

          {/* Reviews / Testimonials */}
          <Card className="lg:col-span-2">
            <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
              <CardTitle className="flex items-center">
                <FileText className="mr-2 h-5 w-5" />
                {language === 'es' ? 'Reseñas / Testimonios' : 'Reviews / Testimonials'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500 mb-3">
                {language === 'es' 
                  ? 'Estas reseñas aparecerán al final de sus propuestas. Déjelas en blanco si no desea incluirlas.'
                  : 'These reviews will appear at the end of your proposals. Leave blank if you don\'t want to include them.'}
              </p>
              <Textarea 
                value={settings.reviews} 
                onChange={(e) => handleChange('reviews', e.target.value)} 
                rows={10} 
                className="font-mono text-sm"
                placeholder={language === 'es' 
                  ? '⭐⭐⭐⭐⭐ "Gran trabajo..." - Cliente Satisfecho'
                  : '⭐⭐⭐⭐⭐ "Great work..." - Satisfied Customer'}
              />
            </CardContent>
          </Card>
        </div>

        {/* Save Button at Bottom */}
        <div className="mt-8 flex justify-center">
          <Button onClick={handleSave} size="lg" style={{ backgroundColor: COMPANY.primaryColor }} className="text-white px-12" disabled={saving}>
            <Save className="mr-2 h-5 w-5" />
            {saving ? (language === 'es' ? 'Guardando...' : 'Saving...') : (language === 'es' ? 'Guardar Configuración' : 'Save Settings')}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Settings;
